import { createReducer } from 'reduxsauce';
import { produce } from 'immer';
import moment from 'moment';
import { clone, concat, filter, findIndex, includes, isEmpty, isNil, merge, propEq, replace, uniqBy, prop, sortBy, toLower, compose } from 'ramda';
import { AppTypes } from '~/actions';
import { convertUrl, removeStatus, version } from '~/services/util';
import { UserRoles } from '~/services/config';

const initialState = {
    status: [],
    version: version(),

    // User information
    token: '',
    user: {},
    created_at: '',
    updated_at: '',
    id: 0,
    avatar: '',
    birthday: '',
    career_goal: '',
    user_name: '',
    first_name: '',
    last_name: '',
    email: '',
    timezone: '',
    job_title: '',
    businesses: [],
    userCompanies: [], // user companies
    companies: [], // Companies that I can access
    employees: [], //HCM employees
    users: [], // Users that I can access
    primary_role_id: UserRoles.EMPLOYEE, // User role for system admin
    app_role_id: UserRoles.EMPLOYEE, // User role for their company
    company_info: {},
    manager_info: {},

    projects: [],

    allAuthors: {},
    userAuthors: [],
    quotaFeeds: [],

    // All available tags for Advanced Search
    authors: [],
    categories: [],
    departments: [],
    competencies: [],

    // Selected tags Advanced Search
    selectedTags: {
        authors: [],
        categories: [],
        departments: [],
        competencies: [],
    },

    // Library search option
    published: true,

    // Company Vendor
    vendor: [],

    // Selected Company, Employee, and User for Global Filter
    selectedCompany: [],
    selectedEmployee: {
        search: '',
        departments: [-1],
        teams: [-1],
        roles: [-1],
        users: [],
    },

    // User notification
    notificationsData: {
        total: 0,
        new: 0,
        viewed: 0,
        status: [],
        notifications: {
            current_page: 1,
            data: [],
            first_page_url: '',
            from: 1,
            last_page: 2,
            last_page_url: '',
            next_page_url: '',
            path: '',
            per_page: 25,
            prev_page_url: null,
            to: 25,
            total: 34,
        },
    },

    jobDescription: { job_descriptions: [] },

    // User health plan data
    userHealthPlan: null,

    //selected Project
    selectedProject: -1,

    // UPLOAD FILE
    learnUploadURL: {},

    // Modals
    modalType: '',
    modalData: {
        before: null,
        after: null,
    },
    modalCallBack: {
        onYes: () => {},
        onNo: () => {},
    },

    // The latest 3 route histories
    locations: ['', '', ''],

    inProfile: 'none',
};

const loginRequest = produce((draft, action) => {
    draft.status.push('pending-alg');
});
const loginSuccess = produce((draft, action) => {
    const { token, user } = action.response;
    const { businesses, community_user, companies, created_at, email, app_role_id, id, company_id, profile, updated_at, company_info, manager_info, primary_role_id } = user;
    const { birthday, career_goal, first_name, last_name, job_title, timezone } = profile;
    const sortByName = sortBy(compose(toLower, prop('name')));
    let _companies = filter((e) => !e.deleted_at, companies);
    _companies = sortByName(_companies);
    draft.status = removeStatus('pending-alg', draft.status);
    draft.token = token;
    draft.user = user;
    draft.created_at = created_at;
    draft.updated_at = updated_at;
    draft.id = id;
    draft.avatar = convertUrl(user.avatar, '/images/default.png');
    draft.birthday = birthday;
    draft.career_goal = career_goal;
    draft.user_name = first_name + ' ' + last_name;
    draft.first_name = first_name;
    draft.last_name = last_name;
    draft.job_title = job_title;
    draft.email = email;
    draft.timezone = timezone;
    draft.businesses = businesses;
    draft.companies = _companies;
    draft.userCompanies = _companies;
    draft.primary_role_id = primary_role_id || UserRoles.USER;
    draft.app_role_id = app_role_id;
    draft.company_info = company_info;
    draft.manager_info = manager_info;
    draft.selectedCompany = [company_id];
    draft.selectedEmployee = {
        search: '',
        department_id: [-1],
        team_id: [-1],
        job_role_id: [-1],
        users: [id],
    };
    draft.users = [{ ...user, name: `${profile.first_name} ${profile.last_name}` }];
});
const loginFailure = produce((draft, action) => {
    draft.status = removeStatus('pending-alg', draft.status);
});

const logoutRequest = produce((draft, action) => {
    draft.status = [];
});
const logoutSuccess = produce((draft, action) => {
    draft.status = [];
});
const logoutFailure = produce((draft, action) => {
    draft.status = [];
});

const refreshRequest = produce((draft, action) => {
    draft.status = [];
});
const refreshSuccess = produce((draft, action) => {
    draft.status = [];
});
const refreshFailure = produce((draft, action) => {
    draft.status = [];
});

const getslugRequest = produce((draft, action) => {
    draft.status = removeStatus('pending-ccs', draft.status);
});
const getslugSuccess = produce((draft, action) => {
    draft.status = removeStatus('pending-ccs', draft.status);
});
const getslugFailure = produce((draft, action) => {
    draft.status = removeStatus('pending-ccs', draft.status);
});

const emailverificationRequest = produce((draft, action) => {
    draft.status.push('pending-evc');
});
const emailverificationSuccess = produce((draft, action) => {
    draft.status = removeStatus('pending-evc', draft.status);
});
const emailverificationFailure = produce((draft, action) => {
    draft.status = removeStatus('pending-evc', draft.status);
});

const getauthoringRequest = produce((draft, action) => {
    draft.status.push('pending-aga');
});
const getauthoringSuccess = produce((draft, action) => {
    draft.status = removeStatus('pending-aga', draft.status);
    draft.userAuthors = action.response;
});
const getauthoringFailure = produce((draft, action) => {
    draft.status = removeStatus('pending-aga', draft.status);
});

const getentityconnectionsRequest = produce((draft, action) => {
    const { loading = true } = action;
    if (loading) draft.status.push('pending-auc');
});
const getentityconnectionsSuccess = produce((draft, action) => {
    draft.status = removeStatus('pending-auc', draft.status);
    const user = {
        ...draft.user,
        community_user: {
            ...draft.user.community_user,
            connections: action.response,
        },
    };
    draft.user = user;
});
const getentityconnectionsFailure = produce((draft, action) => {
    draft.status = removeStatus('pending-auc', draft.status);
});

const getcompanyvendorRequest = produce((draft, action) => {
    draft.status.push('pending-afc');
});
const getcompanyvendorSuccess = produce((draft, action) => {
    draft.status = removeStatus('pending-afc', draft.status);
    draft.vendor = action.response;
});
const getcompanyvendorFailure = produce((draft, action) => {
    draft.status = removeStatus('pending-afc', draft.status);
});

const getlibraryfiltersRequest = produce((draft, action) => {
    draft.status.push('pending-alf');
});
const getlibraryfiltersSuccess = produce((draft, action) => {
    const { authors, categories, competencies, departments } = action.response;
    draft.status = removeStatus('pending-alf', draft.status);
    draft.authors = authors;
    draft.categories = categories;
    draft.competencies = competencies;
    draft.departments = departments;
});
const getlibraryfiltersFailure = produce((draft, action) => {
    draft.status = removeStatus('pending-alf', draft.status);
});

// File upload
const uploadRequest = produce((draft, action) => {
    draft.status.push('pending-aul');
});
const uploadSuccess = produce((draft, action) => {
    draft.status = removeStatus('pending-aul', draft.status);
    draft.learnUploadURL = action.response;
});
const uploadFailure = produce((draft, action) => {
    draft.status = removeStatus('pending-aul', draft.status);
});

const getcompaniesRequest = produce((draft, action) => {
    draft.status.push('pending-agc');
});
const getcompaniesSuccess = produce((draft, action) => {
    draft.status = removeStatus('pending-agc', draft.status);
    draft.companies = action.response;
});
const getcompaniesFailure = produce((draft, action) => {
    draft.status = removeStatus('pending-agc', draft.status);
});

const getusercompaniesSuccess = produce((draft, action) => {
    draft.userCompanies = action.response;
});

const getcompanyRequest = produce((draft, action) => {
    draft.status.push('pending-agm');
});
const getcompanySuccess = produce((draft, action) => {
    draft.status = removeStatus('pending-agm', draft.status);
    draft.companyDetail = action.response;
});
const getcompanyFailure = produce((draft, action) => {
    draft.status = removeStatus('pending-agm', draft.status);
});

const posteditprofileRequest = produce((draft, action) => {
    draft.status.push('pending-ape');
});
const posteditprofileSuccess = produce((draft, action) => {
    const user = action.response;
    let idx = findIndex(propEq('id', user.id), draft.users);
    if (idx < 0) idx = findIndex(propEq('community_user_id', user.id), draft.users);
    if (idx > -1) {
        const firstName = user.first_name || user.profile?.first_name;
        const lastName = user.last_name || user.profile?.last_name;
        draft.users[idx].profile.first_name = firstName;
        draft.users[idx].profile.last_name = lastName;
        draft.users[idx].profile.phone = user.profile?.phone || user.profile?.contact_phone;
        draft.users[idx].profile.about_me = user.profile?.about_me || user.profile?.info_bio;
        draft.users[idx].profile.career_goal = user.profile.career_goal;
        draft.users[idx].name = `${firstName} ${lastName}`;
        draft.users[idx].email = user.email;
        draft.users[idx].email2 = user.email2;

        draft.users[idx].community_user.first_name = firstName;
        draft.users[idx].community_user.last_name = lastName;
    }
    if (draft.id === user.id || draft.user.community_user.id === user.id) {
        draft.user.profile = user.profile;
        draft.user.email = user.email;
        draft.user.email2 = user.email2;
        draft.user.updated_at = user.updated_at || moment().format('YYYY-MM-DD');
        draft.first_name = user.first_name || user.profile?.first_name;
        draft.last_name = user.last_name || user.profile?.last_name;
        draft.user.community_user.first_name = draft.first_name;
        draft.user.community_user.last_name = draft.last_name;
        draft.avatar = user.avatar || user.profile.avatar;
    }
    draft.status = removeStatus('pending-ape', draft.status);
});
const posteditprofileFailure = produce((draft, action) => {
    draft.status = removeStatus('pending-ape', draft.status);
});

const postcompanyusersRequest = produce((draft, action) => {
    draft.status.push('pending-apu');
});
const postcompanyusersSuccess = produce((draft, action) => {
    const users = action.response;
    users.forEach((user) => {
        const idx = findIndex(propEq('id', user.id), draft.users);
        if (idx > -1) {
            draft.users[idx].profile.first_name = user.profile.first_name;
            draft.users[idx].profile.last_name = user.profile.last_name;
            draft.users[idx].profile.phone = user.profile.phone;
            draft.users[idx].profile.about_me = user.profile.about_me;
            draft.users[idx].profile.career_goal = user.profile.career_goal;
            draft.users[idx].name = `${user.profile.first_name} ${user.profile.last_name}`;
            draft.users[idx].email = user.email;
            draft.users[idx].email2 = user.email2;
        } else {
            let newUser = clone(user);
            newUser.name = `${user.profile.first_name} ${user.profile.last_name}`;
            draft.users.push(newUser);
        }
        if (draft.id == user.id) {
            draft.user.profile = user.profile;
            draft.user.email = user.email;
            draft.user.email2 = user.email2;
            draft.user.updated_at = user.updated_at;
            draft.first_name = user.profile.first_name;
            draft.last_name = user.profile.last_name;
            draft.name = user.profile.first_name + ' ' + user.profile.last_name;
            draft.avatar = convertUrl(user.profile.avatar, '/images/default.png');
        }
    });
    draft.status = removeStatus('pending-apu', draft.status);
});
const postcompanyusersFailure = produce((draft, action) => {
    draft.status = removeStatus('pending-apu', draft.status);
});

// Notifications Bell
const fetchnotificationsRequest = produce((draft, action) => {
    draft.status.push('pending-afn');
});
const fetchnotificationsSuccess = produce((draft, action) => {
    const { notifications, total } = action.response;
    let data = clone(draft.notificationsData.notifications.data);
    let notificationsData;

    if (total == 0) {
        notificationsData = {
            ...action.response,
            notifications: {
                ...draft.notificationsData.notifications,
                data: [],
            },
        };
    } else {
        const { current_page, per_page } = notifications;
        const from = (current_page - 1) * Number(per_page);
        data.splice(from, Number(per_page), ...notifications.data);
        data = uniqBy(prop('id'), data);
        notificationsData = {
            ...action.response,
            notifications: {
                ...notifications,
                data,
            },
        };
    }
    draft.status = removeStatus('pending-afn', draft.status);
    draft.notificationsData = notificationsData;
});
const fetchnotificationsFailure = produce((draft, action) => {
    draft.status = removeStatus('pending-afn', draft.status);
});

const updatenotificationsRequest = produce((draft, action) => {
    draft.status.push('pending-aun');
});
const updatenotificationsSuccess = produce((draft, action) => {
    draft.status = removeStatus('pending-aun', draft.status);
});
const updatenotificationsFailure = produce((draft, action) => {
    draft.status = removeStatus('pending-aun', draft.status);
});

const postrecoverypasswordRequest = produce((draft, action) => {
    draft.status.push('pending-apr');
});
const postrecoverypasswordSuccess = produce((draft, action) => {
    draft.status = removeStatus('pending-apr', draft.status);
});
const postrecoverypasswordFailure = produce((draft, action) => {
    draft.status = removeStatus('pending-apr', draft.status);
});

const postmulticompanydataRequest = produce((draft, action) => {
    // draft.status.push('pending-apd')
});
const postmulticompanydataSuccess = produce((draft, action) => {
    // draft.status = removeStatus('pending-apd', draft.status)
    draft.companies = action.response;
    if (action.response.length === 1) draft.users = action.response[0].users;
    let projects = [];
    action.response.forEach((item) => {
        item?.projects?.forEach((project) => {
            projects.push({ ...project, company_name: item.name });
        });
    });
    draft.projects = projects;
});
const postmulticompanydataFailure = produce((draft, action) => {
    // draft.status = removeStatus('pending-apd', draft.status)
});

const postmulticompanyemployeesRequest = produce((draft, action) => {
    const { loading = true } = action;
    if (loading) draft.status.push('pending-apce');
});
const postmulticompanyemployeesSuccess = produce((draft, action) => {
    draft.status = removeStatus('pending-apce', draft.status);
    draft.employees = action.response;
});
const postmulticompanyemployeesFailure = produce((draft, action) => {
    draft.status = removeStatus('pending-apce', draft.status);
});

const getallauthorsRequest = produce((draft, action) => {
    draft.status.push('pending-aat');
});
const getallauthorsSuccess = produce((draft, action) => {
    draft.status = removeStatus('pending-aat', draft.status);
    draft.allAuthors = action.response;
});
const getallauthorsFailure = produce((draft, action) => {
    draft.status = removeStatus('pending-aat', draft.status);
});

const postsaveauthorRequest = produce((draft, action) => {
    draft.status.push('pending-vsa');
});
const postsaveauthorSuccess = produce((draft, action) => {
    draft.status = removeStatus('pending-vsa', draft.status);
});
const postsaveauthorFailure = produce((draft, action) => {
    draft.status = removeStatus('pending-vsa', draft.status);
});

const postsavebusinessauthorRequest = produce((draft, action) => {
    draft.status.push('pending-vsbs');
});
const postsavebusinessauthorSuccess = produce((draft, action) => {
    draft.status = removeStatus('pending-vsbs', draft.status);
});
const postsavebusinessauthorFailure = produce((draft, action) => {
    draft.status = removeStatus('pending-vsbs', draft.status);
});

const postdeleteauthorRequest = produce((draft, action) => {
    draft.status.push('pending-ada');
});
const postdeleteauthorSuccess = produce((draft, action) => {
    draft.status = removeStatus('pending-ada', draft.status);
});
const postdeleteauthorFailure = produce((draft, action) => {
    draft.status = removeStatus('pending-ada', draft.status);
});

// Show Modals
const modalRequest = produce((draft, action) => {
    const { type, data, callBack } = action.payload;
    const { before = {}, after = {} } = data ?? {};

    // When modal type is null, we just update only modal data
    if (isNil(type)) {
        draft.modalData = { before, after };
        return;
    }

    // Confirmation modal
    if (type === 'Confirm') {
        // Show only confirmation modal
        if (isEmpty(draft.modalType) || isNil(draft.modalType)) {
            draft.modalType = type;
            draft.modalData = { before, after };
            draft.modalCallBack = callBack;
            return;
        }

        // Show two modals at the same time
        draft.modalType = includes('Confirm', draft.modalType) ? replace(' - Confirm', '', draft.modalType) : draft.modalType + ' - ' + type;
        draft.modalData = {
            before: {
                ...draft.modalData.before,
                ...callBack, // Confirm expects the callback deconstructed with the onYes & onNo passed as props
                title: before.title,
                body: before.body,
                description: before.description,
                warning: before.warning,
                yesButtonText: before.yesButtonText,
                noButtonText: before.noButtonText,
            },
            after: draft.modalData.after,
        };
        draft.modalCallBack = callBack;
        return;
    }

    if (type === 'Add New Quota') {
        // Show only Add New Quota modal
        if (isEmpty(draft.modalType) || isNil(draft.modalType)) {
            draft.modalType = type;
            draft.modalData = { before, after };
            draft.modalCallBack = callBack;
            return;
        }

        if (includes('- Add New Quota', draft.modalType)) {
            draft.modalType = includes('Add New Quota', draft.modalType)
                ? replace(' - Add New Quota', '', draft.modalType)
                : draft.modalType + ' - ' + type;
        } else {
            draft.modalType = type;
        }

        draft.modalData = merge(draft.modalData, data);
        draft.modalCallBack = callBack;
        return;
    }

    if (type === 'Attach Library') {
        // Show only Attach Library modal
        if (isEmpty(draft.modalType) || isNil(draft.modalType)) {
            draft.modalType = type;
            draft.modalData = { before, after };
            draft.modalCallBack = callBack;
            return;
        }

        if (includes(' - ', draft.modalType)) {
            draft.modalType = type;
            draft.modalData = merge(draft.modalData, data);
            draft.modalCallBack = callBack;
            return;
        }

        // Show two modals at the same time
        draft.modalType = includes('Attach Library', draft.modalType) ? replace(' - Attach Library', '', draft.modalType) : draft.modalType + ' - ' + type;
        draft.modalData = merge(draft.modalData, data);
        draft.modalCallBack = callBack;
        return;
    }

    if (type === 'Add Quota Stars') {
        // Show only Add Quota Stars modal
        if (isEmpty(draft.modalType) || isNil(draft.modalType)) {
            draft.modalType = type;
            draft.modalData = { before, after };
            draft.modalCallBack = callBack;
            return;
        }

        // Show two modals at the same time
        draft.modalType = includes('Add Quota Stars', draft.modalType) ? replace(' - Add Quota Stars', '', draft.modalType) : draft.modalType + ' - ' + type;
        draft.modalData = merge(draft.modalData, data);
        draft.modalCallBack = callBack;
        return;
    }

    if (type === 'Assign Program') {
        // Show only Assign Program modal
        if (isEmpty(draft.modalType) || isNil(draft.modalType)) {
            draft.modalType = type;
            draft.modalData = { before, after };
            draft.modalCallBack = callBack;
            return;
        }

        // Show two modals at the same time
        draft.modalType = includes('Assign Program', draft.modalType) ? replace(' - Assign Program', '', draft.modalType) : draft.modalType + ' - ' + type;
        draft.modalData = merge(draft.modalData, data);
        draft.modalCallBack = callBack;
        return;
    }

    if (type === 'Assign ToDo') {
        // Show only Assign ToDo modal
        if (isEmpty(draft.modalType) || isNil(draft.modalType)) {
            draft.modalType = type;
            draft.modalData = { before, after };
            draft.modalCallBack = callBack;
            return;
        }

        // Show two modals at the same time
        draft.modalType = includes('Assign ToDo', draft.modalType) ? replace(' - Assign ToDo', '', draft.modalType) : draft.modalType + ' - ' + type;
        draft.modalData = merge(draft.modalData, data);
        draft.modalCallBack = callBack;
        return;
    }

    if (type === 'Assign ScoreCard') {
        // Show only Assign ScoreCard modal
        if (isEmpty(draft.modalType) || isNil(draft.modalType)) {
            draft.modalType = type;
            draft.modalData = { before, after };
            draft.modalCallBack = callBack;
            return;
        }

        // Show two modals at the same time
        draft.modalType = includes('Assign ScoreCard', draft.modalType) ? replace(' - Assign ScoreCard', '', draft.modalType) : draft.modalType + ' - ' + type;
        draft.modalData = merge(draft.modalData, data);
        draft.modalCallBack = callBack;
        return;
    }

    if (type === 'Upload Vendor Avatar') {
        // Show only Upload Vendor Avatar modal
        if (isEmpty(draft.modalType) || isNil(draft.modalType)) {
            draft.modalType = type;
            draft.modalData = { before, after };
            draft.modalCallBack = callBack;
            return;
        }

        // Show two modals at the same time
        draft.modalType = includes('Upload Vendor Avatar', draft.modalType) ? replace(' - Upload Vendor Avatar', '', draft.modalType) : draft.modalType + ' - ' + type;
        draft.modalData = merge(draft.modalData, data);
        draft.modalCallBack = callBack;
        return;
    }

    // Close modal or show the other modal normally
    draft.modalType = draft.modalType === type ? '' : type;
    draft.modalData = data;
    draft.modalCallBack = callBack;
});

const getcommunityusersRequest = produce((draft, action) => {
    draft.status.push('pending-aga');
});
const getcommunityusersSuccess = produce((draft, action) => {
    draft.avatar = action.response.user?.avatar;
    draft.status = removeStatus('pending-aga', draft.status);
});
const getcommunityusersFailure = produce((draft, action) => {
    draft.status = removeStatus('pending-aga', draft.status);
});

const getemployeesearchRequest = produce((draft, action) => {
    const { loading = true } = action;
    if (loading) {
        draft.status.push('pending-aes');
    }
});
const getemployeesearchSuccess = produce((draft, action) => {
    draft.users = action.response.users;
    draft.status = removeStatus('pending-aes', draft.status);
});
const getemployeesearchFailure = produce((draft, action) => {
    draft.status = removeStatus('pending-aes', draft.status);
});

const getuserjobdescriptionRequest = produce((draft, action) => {
    draft.status.push('pending-aud');
});
const getuserjobdescriptionSuccess = produce((draft, action) => {
    draft.status = removeStatus('pending-aud', draft.status);
    draft.jobDescription = action.response;
});
const getuserjobdescriptionFailure = produce((draft, action) => {
    draft.status = removeStatus('pending-aud', draft.status);
});

const getuserhealthplanRequest = produce((draft, action) => {
    draft.status.push('pending-auhp');
});
const getuserhealthplanSuccess = produce((draft, action) => {   
    draft.status = removeStatus('pending-auhp', draft.status);
    draft.userHealthPlan = action.response;
});
const getuserhealthplanFailure = produce((draft, action) => {
    draft.status = removeStatus('pending-auhp', draft.status);
});

const advancedsearchRequest = produce((draft, action) => {
    draft.selectedTags = action.tags;
    draft.published = action.published;
    draft.params = action.params;
});

const globalfilterRequest = produce((draft, action) => {
    const { companies, employees } = action;
    draft.selectedCompany = companies;
    draft.selectedEmployee = employees;
});

const selectprojectRequest = produce((draft, action) => {
    draft.selectedProject = action.id;
});

const updateloginuserRequest = produce((draft, action) => {
    const { user } = action;
    draft.user = user;
});

const onprofilepageRequest = produce((draft, action) => {
    draft.inProfile = action.target;
});

const resetRequest = produce((draft) => {
    draft.status = [];
});

const clearRequest = produce((draft, action) => (draft = initialState));

export const reducer = createReducer(initialState, {
    [AppTypes.LOGIN_REQUEST]: loginRequest,
    [AppTypes.LOGIN_SUCCESS]: loginSuccess,
    [AppTypes.LOGIN_FAILURE]: loginFailure,

    [AppTypes.LOGOUT_REQUEST]: logoutRequest,
    [AppTypes.LOGOUT_SUCCESS]: logoutSuccess,
    [AppTypes.LOGOUT_FAILURE]: logoutFailure,

    [AppTypes.REFRESH_REQUEST]: refreshRequest,
    [AppTypes.REFRESH_SUCCESS]: refreshSuccess,
    [AppTypes.REFRESH_FAILURE]: refreshFailure,

    [AppTypes.GETSLUG_REQUEST]: getslugRequest,
    [AppTypes.GETSLUG_SUCCESS]: getslugSuccess,
    [AppTypes.GETSLUG_FAILURE]: getslugFailure,

    [AppTypes.EMAILVERIFICATION_REQUEST]: emailverificationRequest,
    [AppTypes.EMAILVERIFICATION_SUCCESS]: emailverificationSuccess,
    [AppTypes.EMAILVERIFICATION_FAILURE]: emailverificationFailure,

    [AppTypes.GETAUTHORING_REQUEST]: getauthoringRequest,
    [AppTypes.GETAUTHORING_SUCCESS]: getauthoringSuccess,
    [AppTypes.GETAUTHORING_FAILURE]: getauthoringFailure,

    [AppTypes.GETENTITYCONNECTIONS_REQUEST]: getentityconnectionsRequest,
    [AppTypes.GETENTITYCONNECTIONS_SUCCESS]: getentityconnectionsSuccess,
    [AppTypes.GETENTITYCONNECTIONS_FAILURE]: getentityconnectionsFailure,

    [AppTypes.GETLIBRARYFILTERS_REQUEST]: getlibraryfiltersRequest,
    [AppTypes.GETLIBRARYFILTERS_SUCCESS]: getlibraryfiltersSuccess,
    [AppTypes.GETLIBRARYFILTERS_FAILURE]: getlibraryfiltersFailure,

    [AppTypes.UPLOAD_REQUEST]: uploadRequest,
    [AppTypes.UPLOAD_SUCCESS]: uploadSuccess,
    [AppTypes.UPLOAD_FAILURE]: uploadFailure,

    [AppTypes.GETCOMPANIES_REQUEST]: getcompaniesRequest,
    [AppTypes.GETCOMPANIES_SUCCESS]: getcompaniesSuccess,
    [AppTypes.GETCOMPANIES_FAILURE]: getcompaniesFailure,

    [AppTypes.GETUSERCOMPANIES_SUCCESS]: getusercompaniesSuccess,

    [AppTypes.GETCOMPANY_REQUEST]: getcompanyRequest,
    [AppTypes.GETCOMPANY_SUCCESS]: getcompanySuccess,
    [AppTypes.GETCOMPANY_FAILURE]: getcompanyFailure,

    [AppTypes.POSTEDITPROFILE_REQUEST]: posteditprofileRequest,
    [AppTypes.POSTEDITPROFILE_SUCCESS]: posteditprofileSuccess,
    [AppTypes.POSTEDITPROFILE_FAILURE]: posteditprofileFailure,

    [AppTypes.POSTCOMPANYUSERS_REQUEST]: postcompanyusersRequest,
    [AppTypes.POSTCOMPANYUSERS_SUCCESS]: postcompanyusersSuccess,
    [AppTypes.POSTCOMPANYUSERS_FAILURE]: postcompanyusersFailure,

    //Notifications
    [AppTypes.FETCHNOTIFICATIONS_REQUEST]: fetchnotificationsRequest,
    [AppTypes.FETCHNOTIFICATIONS_SUCCESS]: fetchnotificationsSuccess,
    [AppTypes.FETCHNOTIFICATIONS_FAILURE]: fetchnotificationsFailure,

    [AppTypes.UPDATENOTIFICATIONS_REQUEST]: updatenotificationsRequest,
    [AppTypes.UPDATENOTIFICATIONS_SUCCESS]: updatenotificationsSuccess,
    [AppTypes.UPDATENOTIFICATIONS_FAILURE]: updatenotificationsFailure,

    [AppTypes.GETCOMPANYVENDOR_REQUEST]: getcompanyvendorRequest,
    [AppTypes.GETCOMPANYVENDOR_SUCCESS]: getcompanyvendorSuccess,
    [AppTypes.GETCOMPANYVENDOR_FAILURE]: getcompanyvendorFailure,

    [AppTypes.POSTRECOVERYPASSWORD_REQUEST]: postrecoverypasswordRequest,
    [AppTypes.POSTRECOVERYPASSWORD_SUCCESS]: postrecoverypasswordSuccess,
    [AppTypes.POSTRECOVERYPASSWORD_FAILURE]: postrecoverypasswordFailure,

    [AppTypes.POSTMULTICOMPANYDATA_REQUEST]: postmulticompanydataRequest,
    [AppTypes.POSTMULTICOMPANYDATA_SUCCESS]: postmulticompanydataSuccess,
    [AppTypes.POSTMULTICOMPANYDATA_FAILURE]: postmulticompanydataFailure,

    [AppTypes.POSTMULTICOMPANYEMPLOYEES_REQUEST]: postmulticompanyemployeesRequest,
    [AppTypes.POSTMULTICOMPANYEMPLOYEES_SUCCESS]: postmulticompanyemployeesSuccess,
    [AppTypes.POSTMULTICOMPANYEMPLOYEES_FAILURE]: postmulticompanyemployeesFailure,

    [AppTypes.GETALLAUTHORS_REQUEST]: getallauthorsRequest,
    [AppTypes.GETALLAUTHORS_SUCCESS]: getallauthorsSuccess,
    [AppTypes.GETALLAUTHORS_FAILURE]: getallauthorsFailure,

    [AppTypes.POSTSAVEAUTHOR_REQUEST]: postsaveauthorRequest,
    [AppTypes.POSTSAVEAUTHOR_SUCCESS]: postsaveauthorSuccess,
    [AppTypes.POSTSAVEAUTHOR_FAILURE]: postsaveauthorFailure,

    [AppTypes.POSTSAVEBUSINESSAUTHOR_REQUEST]: postsavebusinessauthorRequest,
    [AppTypes.POSTSAVEBUSINESSAUTHOR_SUCCESS]: postsavebusinessauthorSuccess,
    [AppTypes.POSTSAVEBUSINESSAUTHOR_FAILURE]: postsavebusinessauthorFailure,

    [AppTypes.POSTDELETEAUTHOR_REQUEST]: postdeleteauthorRequest,
    [AppTypes.POSTDELETEAUTHOR_SUCCESS]: postdeleteauthorSuccess,
    [AppTypes.POSTDELETEAUTHOR_FAILURE]: postdeleteauthorFailure,

    [AppTypes.GETCOMMUNITYUSERS_REQUEST]: getcommunityusersRequest,
    [AppTypes.GETCOMMUNITYUSERS_SUCCESS]: getcommunityusersSuccess,
    [AppTypes.GETCOMMUNITYUSERS_FAILURE]: getcommunityusersFailure,

    [AppTypes.GETEMPLOYEESEARCH_REQUEST]: getemployeesearchRequest,
    [AppTypes.GETEMPLOYEESEARCH_SUCCESS]: getemployeesearchSuccess,
    [AppTypes.GETEMPLOYEESEARCH_FAILURE]: getemployeesearchFailure,

    [AppTypes.GETUSERJOBDESCRIPTION_REQUEST]: getuserjobdescriptionRequest,
    [AppTypes.GETUSERJOBDESCRIPTION_SUCCESS]: getuserjobdescriptionSuccess,
    [AppTypes.GETUSERJOBDESCRIPTION_FAILURE]: getuserjobdescriptionFailure,

    [AppTypes.GETUSERHEALTHPLAN_REQUEST]: getuserhealthplanRequest,
    [AppTypes.GETUSERHEALTHPLAN_SUCCESS]: getuserhealthplanSuccess,
    [AppTypes.GETUSERHEALTHPLAN_FAILURE]: getuserhealthplanFailure,

    [AppTypes.MODAL_REQUEST]: modalRequest,
    [AppTypes.ADVANCEDSEARCH_REQUEST]: advancedsearchRequest,
    [AppTypes.GLOBALFILTER_REQUEST]: globalfilterRequest,
    [AppTypes.SELECTPROJECT_REQUEST]: selectprojectRequest,
    [AppTypes.UPDATELOGINUSER_REQUEST]: updateloginuserRequest,
    [AppTypes.ONPROFILEPAGE_REQUEST]: onprofilepageRequest,

    [AppTypes.RESET_REQUEST]: resetRequest,

    [AppTypes.CLEAR_REQUEST]: clearRequest,
});
